import SetMap from "../mem/set-map";
import isFreeKeyPress from "../html/is-free-keypress";
import logging from "../logging";
import { registerListener } from "../html/register-listener";

const log = logging('hotkeys');

type Action = string;
type Handler = (event: KeyboardEvent) => void;
type KeyCombo = Set<string>;
export type KeyMapping = {
    // The combo is a space-separated list of key identifiers taken from the
    // list of values for Event.key
    combo: string,
    action: Action,
    // Global mappings have effect even when keyboard focus is in an editable
    // field. For single key mappings like 'o' or 'f', the global flag should
    // be false. For mappings that use modifier keys, like 'Control c',
    // the global flag is probably best set to true.
    global?: boolean,
};

class KeyMapError extends Error {};

/**
 * Given an undordered list of key identifiers (strings), returns an 
 * alphabetized and de-duplicated Set of key identifiers. Guarantees that any
 * equivalent key combination returns the same Set.
 */
const parseKeyCombo = (keys: string[]): KeyCombo => {
    const combo = new Set(keys);
    if(combo.size < keys.length) {
        throw new KeyMapError(
            'Key combo string contains duplicate key identifiers: ['
            + keys.join(', ')
            + ']');
    }

    keys.sort();
    const cacheKey = keys.join('-');
    if(keyComboCache.has(cacheKey)) {
        return keyComboCache.get(cacheKey);
    }

    keyComboCache.set(cacheKey, combo);
    return combo;
}
// This is used by parseKeyCombo to cache sorted key combo sets so that we can
// use the combos as keys in a map and guarantee that referential identity is
// the same as equality of the set members.
const keyComboCache = new Map<string, Set<string>>;

// The HotkeyManager.getInstance() static method will return this instance,
// which it initializes on-demand.
let globalHotkeyManager: HotkeyManager = null;

/**
 * The HotkeyManager allows for global registration of handlers for key presses
 * and key combinations. An 'action' abstraction interposes between the key
 * combination and the handler to support custom remapping, i.e. handlers are
 * mapped to actions and key combinations are mapped to actions, but handlers
 * are never mapped directly to key combinations.
 *
 * Right now this doesn't handle matching arbitrary key sequences. I think that
 * could be delegated to a state machine that consumes the action events. More
 * could be done to validate the keymap. May eventually want to make the key
 * codes case insensitive. Currently the combo 'Shift Y' behaves differently
 * from 'y Shift', i.e. based on the order in which the keys are pressed,
 * because the browser reports the key as 'y' or 'Y' depending on the order.
 *
 * I'm punting on those issues until they actually come up instead of
 * speculating now.
 *
 * For a list of the key names to be used in the key map, see
 * https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
 */
export default class HotkeyManager {
    bypass: boolean = false;

    handlers = new SetMap< Action, Handler >();
    keymap: Map<KeyCombo, {action: Action, global: boolean}> = new Map();

    keysDown: Set<string> = new Set();

    enable(): void {
        registerListener(window, 'keydown', this.keydown);
        registerListener(window, 'keyup', this.keyup);
    }

    disable(): void {
        removeEventListener('keydown', this.keydown);
        removeEventListener('keyup', this.keyup);
    }

    /**
     * The key combo is a space separated string of key identifiers. Their
     * order doesn't matter, but to some degree the order in which they're
     * pressed does, for example 'Shift A' and 'Shift a' will
     * trigger differently depending on the order in which they are pressed
     * because the browser sends us 'A' or 'a' depending on whether shift is
     * down when the 'a' key is pressed. If you don't care about distinguishing
     * these cases, just map both combos to the same action.
     *
     * The key identifiers should match those (case sensitively) used for the
     * KeyboardEvent.key property, which you can find documented online (see
     * the see tag below).
     *
     * An example mapping:
     * hotkeys.addMapping({combo: 'o', action: 'toggle-overhead', global: false});
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
     */
    addMapping(mapping: KeyMapping) {
        const combo = parseKeyCombo(mapping.combo.split(' '));
        this.keymap.set(
            combo,
            {
                action: mapping.action,
                global: mapping.global ?? true
            });
    }

    addMappings(mappings: KeyMapping[]) {
        for(const mapping of mappings) {
            this.addMapping(mapping);
        }
    }

    on(action: Action, handler: Handler) {
        this.handlers.add(action, handler);
    }

    off(action: Action, handler: Handler) {
        const handlers = this.handlers.get(action);
        handlers.delete(handler);
    }

    private keydown = (event: KeyboardEvent) => {
        if(this.bypass) return;
        this.keysDown.add(event.key);
        const size = this.keysDown.size;
        nextCombo:
        for(const [combo, {action, global}] of this.keymap.entries()) {
            // Global mappings don't care if we're in an input field, so if
            // this is not a global mapping, then we must have a free key press
            // i.e. not in an input field
            if(!global && !isFreeKeyPress(event)) {
                continue;
            }

            // If the current count of pressed keys doesn't match the size of
            // the combo, then we couldn't possibly match, so we'll skip to
            // check the next one.
            if(combo.size != size) {
                continue;
            }

            // We can stop checking as soon as we find a non-matching key
            for(const key of this.keysDown) {
                if(!combo.has(key)) {
                    continue nextCombo;
                }
            }

            event.preventDefault();

            // If every key in keysDown is in combo, and keysDown and combo
            // contain the same number of elements, then we trigger the action.
            this.trigger(action, event);

            // Removing the last key from keysDown means that if 'a' and
            // 'ctrl-a' are mapped to actions x and y respectively, then
            // pressing 'a' followed by 'ctrl' would fire action x, whereas
            // pressing 'ctrl' followed 'a' would fire action y. If we didn't
            // clear this, then pressing 'a' followed by 'ctrl' would fire both
            // x and y.
            this.keysDown.delete(event.key);

            // We fire at most one action per key combo
            break;
        }
    }

    private keyup = (event: KeyboardEvent) => {
        if(this.bypass) return;
        this.keysDown.delete(event.key);
    }

    private trigger(action: Action, event: KeyboardEvent) {
        const handlers = this.handlers.get(action);
        for(const fn of handlers) {
            try {
                fn(event);
            } catch(error) {
                log.error(error);
            }
        }
    }

    /**
     * Using a single global hotkey manager makes sense since hotkey actions
     * may span applications.
     */
    static getInstance() {
        if(globalHotkeyManager == null) {
            globalHotkeyManager = new HotkeyManager();
            globalHotkeyManager.enable();
        }
        return globalHotkeyManager;
    }

}
